import React from 'react';

interface AvatarProps {
  name?: string;
  size?: number;
  isCurrentPlayer?: boolean;
  showBorder?: boolean;
  style?: React.CSSProperties;
  onClick?: () => void;
  isMobileOtherPlayer?: boolean; // For reducing size on mobile
}

const Avatar: React.FC<AvatarProps> = ({ 
  name = '', 
  size = 40, 
  isCurrentPlayer = false, 
  showBorder = true,
  style = {},
  onClick,
  isMobileOtherPlayer = false 
}) => {
  // Handle empty/undefined names and "Waiting..." placeholder
  const displayName = name && name !== 'Waiting...' && name.trim() !== '' ? name : '';
  const initial = displayName?.charAt(0)?.toUpperCase() || '?';
  
  // Reduce size by 20% for mobile other players
  const effectiveSize = isMobileOtherPlayer ? size * 0.8 : size;
  
  // Modern gradient backgrounds
  const getGradientBackground = () => {
    if (isCurrentPlayer) {
      return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    }
    
    // Generate subtle gradient based on name hash for variety
    const hash = displayName ? displayName.split('').reduce((acc, char) => {
      return acc + char.charCodeAt(0);
    }, 0) : 0;
    
    const gradients = [
      'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
      'linear-gradient(135deg, #fd79a8 0%, #e84393 100%)',
      'linear-gradient(135deg, #fdcb6e 0%, #e17055 100%)',
      'linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%)',
      'linear-gradient(135deg, #00cec9 0%, #00b894 100%)',
      'linear-gradient(135deg, #fab1a0 0%, #e17055 100%)',
    ];
    
    return gradients[hash % gradients.length];
  };

  const avatarStyle: React.CSSProperties = {
    width: effectiveSize,
    height: effectiveSize,
    borderRadius: '50%',
    background: getGradientBackground(),
    border: showBorder ? (isCurrentPlayer ? '2px solid rgba(102, 126, 234, 0.4)' : '1px solid rgba(255, 255, 255, 0.15)') : 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: effectiveSize * 0.4,
    fontWeight: 600,
    cursor: onClick ? 'pointer' : 'default',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: isCurrentPlayer 
      ? '0 8px 25px rgba(102, 126, 234, 0.25), 0 0 0 1px rgba(102, 126, 234, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.15)'
      : '0 4px 12px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
    position: 'relative',
    overflow: 'hidden',
    ...style
  };

  // Hover and active states
  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    if (onClick) {
      e.currentTarget.style.transform = 'scale(1.05)';
      e.currentTarget.style.boxShadow = isCurrentPlayer 
        ? '0 12px 30px rgba(102, 126, 234, 0.35), 0 0 0 1px rgba(102, 126, 234, 0.2)'
        : '0 8px 20px rgba(0, 0, 0, 0.25)';
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    if (onClick) {
      e.currentTarget.style.transform = 'scale(1)';
      e.currentTarget.style.boxShadow = avatarStyle.boxShadow as string;
    }
  };

  return (
    <div 
      style={avatarStyle}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Subtle inner shine effect */}
      <div style={{
        position: 'absolute',
        top: '10%',
        left: '20%',
        width: '30%',
        height: '30%',
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.1) 100%)',
        borderRadius: '50%',
        filter: 'blur(1px)',
        pointerEvents: 'none'
      }} />
      
      {/* Avatar text */}
      <span style={{ 
        position: 'relative', 
        zIndex: 1,
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)' 
      }}>
        {initial}
      </span>
    </div>
  );
};

export default Avatar;